import React from 'react';
import { Form } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useForm, Controller } from 'react-hook-form';
import {
  FormSection,
  StyledInput,
  SaveChangesButton,
  CancelButton,
  ActionButtons,
  FieldHint,
  AddDomainFormWrapper,
  DomainTypeOptionsRow,
  DomainTypeOption,
  DomainTypeOptionHeader,
  DomainTypeRadioDot,
  DomainTypeOptionTitle,
  DomainTypeOptionExample,
} from '../Styles';
import { isValidDomain, normalizeDomain } from '../constants';
import type { AddDomainFormProps, DomainFormValues } from '../types';

const AddDomainForm: React.FC<AddDomainFormProps> = ({ loading, onSave, onCancel }) => {
  const { handleSubmit, control } = useForm<DomainFormValues>({
    defaultValues: {
      domain: '',
      domainType: 'root_domain',
    },
  });

  const onSubmit = (data: DomainFormValues) => {
    onSave({
      domain: normalizeDomain(data.domain),
      domainType: data.domainType,
    });
  };

  return (
    <AddDomainFormWrapper>
      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <FormSection>

          <Controller
            name="domain"
            control={control}
            rules={{
              required: 'Please enter a domain name.',
              validate: (v) => isValidDomain(normalizeDomain(v)) || 'Please enter a valid domain (e.g. myrestaurant.com).',
            }}
            render={({ field, fieldState }) => (
              <Form.Item
                label="Domain"
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message ?? <FieldHint>Enter without http:// or https://</FieldHint>}
              >
                <StyledInput {...field} placeholder="e.g. myrestaurant.com or order.myrestaurant.com" />
              </Form.Item>
            )}
          />

          <Controller
            name="domainType"
            control={control}
            render={({ field }) => (
              <Form.Item label="Domain Type">
                <DomainTypeOptionsRow>
                  <DomainTypeOption
                    $selected={field.value === 'root_domain'}
                    onClick={() => field.onChange('root_domain')}
                  >
                    <DomainTypeOptionHeader>
                      <DomainTypeRadioDot $selected={field.value === 'root_domain'} />
                      <DomainTypeOptionTitle>Root Domain</DomainTypeOptionTitle>
                    </DomainTypeOptionHeader>
                    <DomainTypeOptionExample>e.g. myrestaurant.com</DomainTypeOptionExample>
                  </DomainTypeOption>

                  <DomainTypeOption
                    $selected={field.value === 'subdomain'}
                    onClick={() => field.onChange('subdomain')}
                  >
                    <DomainTypeOptionHeader>
                      <DomainTypeRadioDot $selected={field.value === 'subdomain'} />
                      <DomainTypeOptionTitle>Subdomain</DomainTypeOptionTitle>
                    </DomainTypeOptionHeader>
                    <DomainTypeOptionExample>e.g. order.myrestaurant.com</DomainTypeOptionExample>
                  </DomainTypeOption>
                </DomainTypeOptionsRow>
              </Form.Item>
            )}
          />

          <ActionButtons>
            <SaveChangesButton icon={<CheckOutlined />} htmlType="submit" loading={loading}>
              Add Domain
            </SaveChangesButton>
            <CancelButton onClick={onCancel}>Cancel</CancelButton>
          </ActionButtons>

        </FormSection>
      </Form>
    </AddDomainFormWrapper>
  );
};

export default AddDomainForm;
